// @ts-strict-ignore
import { FloatingServiceMenu, Footer, Header, Page, PageTransition } from '@components/Page'
import CartItemList from '@components/_cart/CartItemList'
import CartSummary from '@components/_cart/CartSummary'
import EmptyCart from '@components/_cart/EmptyCart'
import { CartRecommendedProducts as RecommendedProducts } from '@components/_cart/RecommendedProducts'
import StickySummary from '@components/_cart/StickySummary'
import CartPrivilegeItemList from '@components/_cart/privilege-item-list'
import {
  Box,
  CheckoutLink,
  Container,
  Flex,
  Grid,
  OldHeading,
  OldText,
  breakpoints,
} from '@gassan-ui'
import { CartItem, PrivilegeCartItem, useUpdateCartItemQuantityMutation } from '@generated'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { useEmarsysTracking } from '@lib/emarsys-tracking/use-emarsys-tracking'
import { formatPrice } from '@lib/formatPrice'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { useBreakpoint } from '@lib/hooks/useBreakpoint'
import { useCart } from '@lib/hooks/use-cart'
import { useEmarsysScriptIsLoaded, useEmarsysScarabQueueIsLoaded } from '@lib/use-emarsys-store'
import { AnimatePresence } from 'framer-motion'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'

const Cart: NextPage = () => {
  const { cart } = useCart()
  const { t } = useTranslation('other')
  const [gridRef, gridInView] = useInView()
  const [summaryRef, summaryInView] = useInView()
  const [loadingRow, setLoadingRow] = useState<string | null>(null)
  const [isRendered, setRendered] = useState(false)
  const scriptIsLoaded = useEmarsysScriptIsLoaded()
  const scarabQueueIsLoaded = useEmarsysScarabQueueIsLoaded()
  const { isTrackingReady, setShoppingCartVisit, setDefaultSettings, go } = useEmarsysTracking()
  const router = useRouter()
  const isDesktop = useBreakpoint(breakpoints.large)
  const total = cart ? cart.totalItems : 0

  const [, updateCartItemQuantity] = useUpdateCartItemQuantityMutation()

  const handleChangeQuantity = async (cartItemId: string, quantity: number) => {
    setLoadingRow(cartItemId)
    try {
      await updateCartItemQuantity({ reference: cart.id, cartItemId, quantity })
      setLoadingRow(null)
    } catch (error) {
      // Show error notification
      setLoadingRow(null)
    }
  }

  useEffect(() => {
    setRendered(true)
  }, [])

  useEffect(() => {
    if (scriptIsLoaded && scarabQueueIsLoaded && isTrackingReady) {
      setDefaultSettings()
      setShoppingCartVisit()
      go(['recommend'])
    }
  }, [scriptIsLoaded, scarabQueueIsLoaded, isTrackingReady]) // eslint-disable-line

  const locales = useRouter().locales
  return (
    <Page
      title={t('shoppingBag')}
      languageAlternates={buildLanguageAlternateNavigation(locales, 'secondaryNav.cart.url')}
    >
      <FloatingServiceMenu
        variant={
          !summaryInView && gridInView && isRendered && !isDesktop ? 'move-element-up' : 'default'
        }
      />
      <Header initialVariant="absolute" />
      <PageTransition>
        <Container>
          <Box
            pt={{ _: '1rem', small: '1.5rem', large: '2.5rem', xlarge: '3.5rem' }}
            pb={{ _: '1.75rem', small: '1.5rem', large: '2rem' }}
          >
            <CheckoutLink onClick={() => router.back()} title={t('continueShopping')} />
            <Flex
              mt={{ _: '0.5rem', small: '2rem', large: '2.5rem' }}
              justifyContent="space-between"
              alignItems="center"
            >
              <OldHeading variant="h1" as="h1" mb="0">
                {t('yourShoppingBag')}
              </OldHeading>
              <Box display={['none', 'contents']}>
                {cart && (
                  <OldText variant="large" color="shades.500" mb="0">
                    {`${total} ${total === 1 ? t('article') : t('articles')}`}
                  </OldText>
                )}
              </Box>
            </Flex>
          </Box>
          {!cart ? (
            <Box minHeight={500}></Box>
          ) : cart?.items.length === 0 && cart?.privilegeItems.length === 0 ? (
            <EmptyCart />
          ) : (
            <>
              <Grid
                ref={gridRef}
                gridTemplateColumns={{ _: '1fr', large: '1fr 28rem' }}
                gridColumnGap={{ _: '2rem', xxlarge: '5rem' }}
                alignItems="flex-start"
              >
                <div>
                  {cart?.privilegeItems.length > 0 && (
                    <CartPrivilegeItemList
                      items={cart.privilegeItems as PrivilegeCartItem[]}
                      loadingRow={loadingRow}
                    />
                  )}
                  {cart?.items.length > 0 && (
                    <CartItemList
                      items={cart.items as CartItem[]}
                      loadingRow={loadingRow}
                      handleChangeQuantity={(cartItemId, quantity) =>
                        handleChangeQuantity(cartItemId, quantity)
                      }
                    />
                  )}
                </div>
                <Box ref={summaryRef} suppressHydrationWarning>
                  <CartSummary status={loadingRow ? 'updating' : 'idle'} />
                </Box>
              </Grid>
              <Box display={{ _: ' none', large: 'block' }} mt="2.5rem">
                <CheckoutLink title={t('continueShopping')} onClick={() => router.back()} />
              </Box>
              <AnimatePresence>
                {!summaryInView && gridInView && isRendered && !isDesktop && (
                  <StickySummary
                    price={formatPrice(cart.totalPriceIncTax, true)}
                    status={loadingRow ? 'updating' : 'idle'}
                  />
                )}
              </AnimatePresence>
            </>
          )}
        </Container>
        <RecommendedProducts />
        <Footer />
      </PageTransition>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await getServerSideTranslations(locale)),
    },
  }
}

export default Cart
